<template>
  <div class="table-container">
    <div class="level">
      <div class="level-left">
        <h3 class="title is-6">Error Logs / Failed Submissions</h3>
      </div>
      <div class="level-right">
        <div class="field is-grouped">
          <div class="control">
            <button 
              class="button is-small is-info" 
              @click="refreshLogs"
              :disabled="loading"
            >
              <span class="icon is-small">
                <i class="fas fa-sync-alt" :class="{ 'fa-spin': loading }"></i>
              </span>
              <span>Refresh</span>
            </button>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Loading state -->
    <div v-if="loading" class="notification is-info is-light">
      <span class="icon">
        <i class="fas fa-spinner fa-spin"></i>
      </span>
      <span>Loading error logs...</span>
    </div>
    
    <!-- Data table -->
    <div v-else class="table-wrapper">
      <table class="table is-fullwidth is-striped is-hoverable">
        <thead>
          <tr>
            <th class="is-size-7">Invoice No</th>
            <th class="is-size-7">Date</th>
            <th class="is-size-7">Error Type</th>
            <th class="is-size-7">Error Message</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="errorData.length === 0">
            <td colspan="4" class="has-text-centered has-text-grey">
              <span class="icon">
                <i class="fas fa-check-circle"></i>
              </span>
              <span class="ml-2">No error logs found</span>
            </td>
          </tr>
          <tr v-for="error in errorData" :key="error.id">
            <td class="is-size-7">{{ error.related_invoice || 'N/A' }}</td>
            <td class="is-size-7">{{ formatDate(error.timestamp) }}</td>
            <td>
              <span class="tag is-light is-size-7">{{ error.error_type_display }}</span>
            </td>
            <td>
              <span class="error-message is-size-7" :title="error.cleansed_message">
                {{ truncateText(error.cleansed_message, 50) }}
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <nav class="pagination is-centered is-small" v-if="pagination.count > pagination.page_size">
      <button 
        class="pagination-previous" 
        :disabled="!pagination.previous || loading"
        @click="changePage(currentPage - 1)"
      >
        Previous
      </button>
      <button 
        class="pagination-next" 
        :disabled="!pagination.next || loading"
        @click="changePage(currentPage + 1)"
      >
        Next page
      </button>
      <ul class="pagination-list">
        <li v-for="page in visiblePages" :key="page">
          <button 
            class="pagination-link"
            :class="{ 'is-current': page === currentPage }"
            :disabled="loading"
            @click="changePage(page)"
          >
            {{ page }}
          </button>
        </li>
      </ul>
    </nav>
  </div>
</template>

<script>
export default {
  name: 'ErrorLogsTable',
  props: {
    data: {
      type: Object,
      default: () => ({ results: [], count: 0, next: null, previous: null })
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      resubmitting: [],
      currentPage: 1
    }
  },
  computed: {
    errorData() {
      console.log('ErrorLogsTable - data prop:', this.data);
      console.log('ErrorLogsTable - results:', this.data.results);
      return this.data.results || [];
    },
    pagination() {
      return {
        count: this.data.count || 0,
        next: this.data.next,
        previous: this.data.previous,
        page_size: 10
      };
    },
    totalPages() {
      return Math.ceil(this.pagination.count / this.pagination.page_size);
    },
    visiblePages() {
      const pages = [];
      const start = Math.max(1, this.currentPage - 2);
      const end = Math.min(this.totalPages, this.currentPage + 2);
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      return pages;
    }
  },
  methods: {
    formatDate(timestamp) {
      if (!timestamp) return 'N/A';
      try {
        const date = new Date(timestamp);
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        });
      } catch (error) {
        return timestamp;
      }
    },
    getStatusClass(status) {
      const statusClasses = {
        'Submission Failed': 'is-danger',
        'Validation Failed': 'is-warning',
        'Error': 'is-danger'
      };
      return statusClasses[status] || 'is-light';
    },
    truncateText(text, length) {
      if (!text) return 'No error message available';
      return text.length > length ? text.substring(0, length) + '...' : text;
    },
    handleResubmit(invoiceNumber) {
      this.resubmitting.push(invoiceNumber);
      this.$emit('resubmit', invoiceNumber);
      
      // Remove from resubmitting array after 3 seconds (adjust as needed)
      setTimeout(() => {
        const index = this.resubmitting.indexOf(invoiceNumber);
        if (index > -1) {
          this.resubmitting.splice(index, 1);
        }
      }, 3000);
    },
    viewStackTrace(error) {
      // Emit event to show stack trace in a modal or detailed view
      this.$emit('view-details', error);
    },
    changePage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
        this.$emit('page-change', page);
      }
    },
    refreshLogs() {
      this.$emit('refresh');
    }
  }
}
</script>

<style scoped>
.table-container {
  background: white;
  border-radius: 8px;
  padding: 1.5rem;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.table-wrapper {
  overflow-x: auto;
}

.error-message {
  cursor: help;
}

.pagination {
  margin-top: 1rem;
}
</style>